import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './WastageReport.module.css';
import Header from '../../Header';
import { useNavigate } from 'react-router-dom';
import { local_signage, local_url } from '../../Const';

export const WastageReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month')); // 1 month before today
  const [toDate, setToDate] = useState(dayjs()); // Today's date
  const [searchTerm, setSearchTerm] = useState('');
  
  // NEW: Add State for Segments, Areas, Branches, and BranchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  
  // State for table
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Session timeout dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // FIXED: Helper functions to handle date without UTC conversion
  const parseLocalDate = (dateString) => {
    if (!dateString) return new Date();
    
    try {
      // Remove the 'Z' and treat as local time (IST) instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      return isNaN(date.getTime()) ? new Date() : date;
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return new Date();
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDate(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return '';
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDate(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time:', dateString, error);
      return '';
    }
  };

  // Function to clear all localStorage and navigate to login
  const handleSessionTimeout = () => {
    // Clear all localStorage items
    localStorage.clear();
    
    // Close the dialog
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    // Navigate to login page
    navigate('/Login', { replace: true });
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000); // 60 seconds

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  // Session Timeout Dialog Component
  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}} // Prevent closing by clicking outside
      disableEscapeKeyDown // Prevent closing with escape key
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Date picker styling
  const datePickerStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dee2e6',
      },
      '&:hover fieldset': {
        borderColor: '#adb5bd',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#6c757d',
    },
  };

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '40%',
            height: '40%',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading Wastage Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we load wastage report data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              ${theme.palette.primary.main}, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching business segments:', error);
        }
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      setLoadingAreas(true);
      try {
        const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          // Add "ALL" option to areas and use SFAreaId and SFAreaName from API response
          const areasWithAll = [
            { SFAreaId: 'ALL', SFAreaName: 'ALL' },
            ...data
          ];
          setAreas(areasWithAll);
          setSelectedAreaObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching areas by segment:', error);
          setAreas([]);
        }
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      setLoadingBranches(true);
      try {
        // Use SFAreaId from the selected area object
        const areaParam = selectedAreaObj.SFAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await apiCall(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching branch list:', error);
          setBranches([]);
        }
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  // Fetch wastage report data from API with 401 handling
  const fetchWastageReportData = async (areaId, branchId, fromDate, toDate) => {
    try {
      const startDate = fromDate ? fromDate.format('YYYYMMDD') : '20240101';
      const endDate = toDate ? toDate.format('YYYYMMDD') : '20250101';

      const response = await apiCall(
        `${local_signage}/api/v1/wastagereport?BizSegmentId=${branchType || 'ALL'}&AreaID=${areaId}&BranchID=${branchId}&ItemID=ALL&StartDate=${startDate}&EndDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        return transformApiData(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch wastage report data:', response.status, errorText);
        alert(`Failed to fetch data: ${response.status} - ${errorText}`);
        return [];
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching wastage report data:', error);
        alert('Error fetching data. Please check your connection and try again.');
      }
      return [];
    }
  };

  // FIXED: Transform API data with proper date handling (treating UTC as local IST)
  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) {
      console.error('API data is not an array:', apiData);
      return [];
    }

    return apiData.map((item, index) => {
      try {
        return {
          id: index + 1,
          sno: index + 1,
          // FIXED: Use formatDateOnly for consistent DD/MM/YYYY formatting
          date: formatDateOnly(item.BusinessDate),
          time: formatTimeOnly(item.BusinessDate),
          area: item.AreaName || '',
          storeCode: item.BranchId || '',
          storeName: item.BranchName || '',
          itemCode: item.ItemID || '',
          itemName: item.itemname || '',
          itemEA: Number(item.EA) || 0,
          qtyWasted: Number(item.KG) || Number(item.qty) || 0,
          wastageType: item.WastageReasonName || '',
          uom: Number(item.KG) > 0 ? 'KG' : 'EA',
          batchNumber: item.BatchNumber || '',
          // Additional fields from API
          transTypeId: item.TransTypeID || '',
          // remarks: item.Remarks || '',
          lineNumber: item.LineNumber || '',
          altQty: Number(item.AltQty) || 0,
          itemGroup: item.ItemFamilyName || 'General'
        };
      } catch (error) {
        console.error('Error transforming item:', item, error);
        // Return a default object for failed transformations
        return {
          id: index + 1,
          sno: index + 1,
          date: new Date().toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }),
          time: new Date().toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }),
          area: item.AreaName || 'N/A',
          storeCode: item.BranchId || 'N/A',
          storeName: item.BranchName || 'N/A',
          itemCode: item.ItemID || 'N/A',
          itemName: item.itemname || 'N/A',
          itemEA: 0,
          qtyWasted: 0,
          wastageType: item.WastageReasonName || 'N/A',
          uom: 'EA',
          batchNumber: item.BatchNumber || '',
          transTypeId: item.TransTypeID || '',
          // remarks: item.Remarks || '',
          lineNumber: item.LineNumber || '',
          altQty: 0,
          itemGroup: 'General'
        };
      }
    });
  };

  // Initialize data
  useEffect(() => {
    // Initialize with empty data, will be populated when filters are applied
    setRawData([]);
    setFilteredData([]);
  }, []);

  // Apply filters
  const applyFilters = async () => {
    if (!branchType) {
      alert('Please select a Segment to fetch data.');
      return;
    }

    if (!selectedAreaObj) {
      alert('Please select an Area to fetch data.');
      return;
    }

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be after To Date.');
      return;
    }

    setLoading(true);

    // Reset sorting and pagination when applying filters
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);

    try {
      const branchId = selectedBranchObj ? selectedBranchObj.branchID : 'ALL';

      console.log('Fetching wastage data with params:', {
        areaId: selectedAreaObj.SFAreaId,
        branchId,
        fromDate: fromDate.format('YYYYMMDD'),
        toDate: toDate.format('YYYYMMDD')
      });

      const data = await fetchWastageReportData(
        selectedAreaObj.SFAreaId,
        branchId,
        fromDate,
        toDate
      );

      console.log('Received wastage data:', data);

      setRawData(data);
      setFilteredData(data);

      if (data.length === 0) {
        alert('No data found for the selected criteria.');
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error applying filters:', error);
        alert('Error fetching data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedArea('');
    setSelectedBranch('');
    setBranchType('');
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setFromDate(dayjs().subtract(1, 'month')); // Reset to 1 month before today
    setToDate(dayjs()); // Reset to today
    setSearchTerm('');
    setRawData([]);
    setFilteredData([]);
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Export function
  const exportToXLSX = () => {
    if (filteredAndSearchedData.length === 0) {
      alert('No data to export.');
      return;
    }

    const exportData = filteredAndSearchedData.map(row => ({
      'Date': row.date,
      // 'Time': row.time,
      'Area': row.area,
      'Store Code': row.storeCode,
      'Store Name': row.storeName,
      'Item Code': row.itemCode,
      'Item Name': row.itemName,
      'Item Group': row.itemGroup,
      'Item EA': row.itemEA,
      'Qty Wasted': row.qtyWasted,
      'Wastage Type': row.wastageType,
      'UOM': row.uom,
      'Batch Number': row.batchNumber,
      // 'Remarks': row.remarks
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Wastage Report');
    XLSX.writeFile(wb, 'wastage_report.xlsx');
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Search and sort data
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase().trim();
      data = data.filter(item =>
        (item.itemName && item.itemName.toString().toLowerCase().includes(lower)) ||
        (item.itemCode && item.itemCode.toString().toLowerCase().includes(lower)) ||
        (item.wastageType && item.wastageType.toString().toLowerCase().includes(lower)) ||
        (item.storeName && item.storeName.toString().toLowerCase().includes(lower)) ||
        (item.storeCode && item.storeCode.toString().toLowerCase().includes(lower)) ||
        (item.itemGroup && item.itemGroup.toString().toLowerCase().includes(lower))
      );
    }

    if (!sortConfig.key) {
      return data.map((item, index) => ({
        ...item,
        sno: index + 1
      }));
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }).map((item, index) => ({
      ...item,
      sno: index + 1 // Preserve sequential S.No regardless of sorting
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Auto-redirect to first page when search results change
  useEffect(() => {
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <Header/>
      
      <div className={styles.wastage_dashboard}>
        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />

        {/* Filters Section */}
        <div className={styles.wastage_filters_section}>
          <div className={styles.wastage_filters_grid}>
            {/* NEW: Segment Dropdown */}
            <div className={styles.wastage_filter_group}>
              <Autocomplete
                options={segments}
                getOptionLabel={(option) => option.businessSegmentName || ''}
                value={segments.find(seg => seg.businessSegmentCode === branchType) || null}
                onChange={(event, newValue) => setBranchType(newValue ? newValue.businessSegmentCode : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Segment"
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* NEW: Area Dropdown with updated logic */}
            <div className={styles.wastage_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => setSelectedAreaObj(newValue)}
                disabled={!branchType || loadingAreas}
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* NEW: Branch Dropdown with updated logic */}
            <div className={styles.wastage_filter_group}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.branchName || ''}
                value={selectedBranchObj}
                onChange={(event, newValue) => setSelectedBranchObj(newValue)}
                disabled={!selectedAreaObj || loadingBranches}
                loading={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingBranches ? "Loading Branches..." : "Select Branch"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            <div className={styles.wastage_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={datePickerStyle}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.wastage_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={datePickerStyle}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className={styles.wastage_filter_actions}>
            <button onClick={applyFilters} className={`${styles.wastage_btn} ${styles.wastage_btn_primary}`} disabled={loading}>
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button onClick={clearFilters} className={`${styles.wastage_btn} ${styles.wastage_btn_secondary}`}>
              Clear
            </button>
            <button onClick={exportToXLSX} className={`${styles.wastage_btn} ${styles.wastage_btn_success}`}>
              Export XLSX
            </button>
            <div className={styles.wastage_search_group}>
              <input
                type="text"
                placeholder="Search by Item Name, Code, Wastage Type, Store..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.wastage_search_input}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={styles.wastage_results_summary}>
          <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
          <div className={styles.wastage_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.wastage_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section */}
        <div className={styles.wastage_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.wastage_no_data}>
              <p>No data found. Please select a Segment and Area, then click Apply to fetch data.</p>
            </div>
          ) : (
            <div className={styles.wastage_table_container}>
              <table className={styles.wastage_data_table}>
                <thead>
                  <tr>
                    <th className={styles.wastage_non_sortable}>
                      S.No
                    </th>
                    <th onClick={() => handleSort('date')} className={styles.wastage_sortable}>
                      Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    {/* <th onClick={() => handleSort('time')} className={styles.wastage_sortable}>
                      Time {sortConfig.key === 'time' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th> */}
                    <th onClick={() => handleSort('area')} className={styles.wastage_sortable}>
                      Area {sortConfig.key === 'area' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('storeCode')} className={styles.wastage_sortable}>
                      Store Code {sortConfig.key === 'storeCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Store Name</th>
                    <th onClick={() => handleSort('itemCode')} className={styles.wastage_sortable}>
                      Item Code {sortConfig.key === 'itemCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Item Name</th>
                    <th>Item Group</th>
                    <th onClick={() => handleSort('itemEA')} className={styles.wastage_sortable}>
                      Item EA {sortConfig.key === 'itemEA' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('qtyWasted')} className={styles.wastage_sortable}>
                      Qty Wasted {sortConfig.key === 'qtyWasted' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('wastageType')} className={styles.wastage_sortable}>
                      Wastage Type {sortConfig.key === 'wastageType' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>UOM</th>
                    <th>Batch Number</th>
                    {/* <th>Remarks</th> */}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.sno}</td>
                      <td>{row.date}</td>
                      {/* <td>{row.time}</td> */}
                      <td className={styles.wastage_capitalize}>{row.area}</td>
                      <td className={styles.wastage_ticket_id}>{row.storeCode}</td>
                      <td>{row.storeName}</td>
                      <td className={styles.wastage_ticket_id}>{row.itemCode}</td>
                      <td>{row.itemName}</td>
                      <td>{row.itemGroup}</td>
                      <td className={styles.wastage_amount}>{row.itemEA}</td>
                      <td className={styles.wastage_amount}>{row.qtyWasted}</td>
                      <td>{row.wastageType}</td>
                      <td>{row.uom}</td>
                      <td>{row.batchNumber}</td>
                      {/* <td>{row.remarks}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.wastage_pagination}>
            {/* First Page Button */}
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`${styles.wastage_btn} ${styles.wastage_btn_pagination}`}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`${styles.wastage_btn} ${styles.wastage_btn_pagination}`}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div className={styles.wastage_page_numbers}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`${styles.wastage_btn} ${styles.wastage_btn_pagination} ${currentPage === page ? styles.wastage_active : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`${styles.wastage_btn} ${styles.wastage_btn_pagination}`}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`${styles.wastage_btn} ${styles.wastage_btn_pagination}`}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default WastageReport;